import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import "./AppDrawerNav.css";
import { Alert, Box, CssBaseline, Snackbar } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { resetUserComplete } from "../../features/auth/authSlice";
import { useNavigate } from "react-router";
import {
  getBotbyID,
  getBotsbyUser,
  getCombinations,
  getFinetuningCombinations,
  getgenerationCombinations,
  resetChatbot,
} from "../../features/chatbots/chatbotSlice";
import Loader from "../../Components/Loader";
import SettingsPage from "./ChatBotRetrievalDocs/SettingsPage/SettingsPage";
import Management from "./UserManagement/Management";
import {
  getOrganization,
  get_organization_by_user,
} from "../../features/organization/orgSlice";
import {
  applyNodeChanges,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from "reactflow";
import UserProfile from "./UserManagement/UserProfile";
import DeployedBots from "./DeployedBots/DeployedBots";
import ContactUs from "./ContactUs/ContactUs";
import InputNodes from "./ChatBotRetrievalDocs/RetrievalBot/SubComponents/InputNodes/InputNodes";
import UploadDatasetsNode from "./ChatBotRetrievalDocs/RetrievalBot/SubComponents/UploadDatasetsNode/UploadDatasetsNode";
import GoldenResponseNode from "./ChatBotRetrievalDocs/RetrievalBot/SubComponents/GoldenResponseNode/GoldenResponseNode";
import ProblemStatementNode from "./ChatBotRetrievalDocs/RetrievalBot/SubComponents/ProblemStatementNode/ProblemStatementNode";
import LLMNode from "./ChatBotRetrievalDocs/RetrievalBot/SubComponents/LLMNode/LLMNode";
import QueryloopNode from "./ChatBotRetrievalDocs/RetrievalBot/SubComponents/RAGHyperparameters/RAGHyperparameters";
import BirectionalEdge from "./ChatBotRetrievalDocs/Edges/BirectionalEdge";
import { clearDb, isDatabaseEmpty } from "../../utils/IndexedDBDexieUtility";
import SideBar from "../../Components/SideBar/SideBar";
import LogRocket from "logrocket";
import Onboarding from "../../Components/Onboarding/Onboarding";
import { sendBugReport } from "../../utils/sendBugReport";
import Templates from "../Main/Templates/Templates";
import AppSidebar from "./AppDrawerComponent";
import {
  getInitialInputSteps,
  getInitialRetrievalSteps,
  handleDragDropSuccessGen,
  handleDragDropSuccessRet,
  handleGenerationTutorial,
  handleInputsTutorial,
  handleRetrievalTutorial,
  startTour,
} from "../../utils/InputTutorials";
import {
  drawerContainer,
  drawerMain,
  drawerSubContainer,
} from "./AppDrawerNavStyles";
import {
  CreateChatBotSnackBarGeneral,
  GraphRAGSnackBar,
} from "../../utils/SnackBars";
import {
  PaymentSuccessDialog,
  StarterLimitationsDialog,
} from "./AppDrawerNavDialogs";

import {
  botDataDefault,
  generationDefault,
  retrievalDefault,
} from "../../utils/globalBotData";

const BotType = lazy(() =>
  import("./ChatBotRetrievalDocs/RetrievalBot/BotType")
);
const PostGrid = lazy(() =>
  import("./ChatBotRetrievalDocs/RetrievalBot/PostGrid/PostGrid")
);

const initialNodesTypes = {
  InputNodes: InputNodes,
  datasets: UploadDatasetsNode,

  goldenResponse: GoldenResponseNode,
  problemStatement: ProblemStatementNode,
  llm: LLMNode,
  QueryloopNode: QueryloopNode,
};

const initialEdgeTypes = {
  bidirectionalEdge: BirectionalEdge,
};

const STORAGE_EVENT_KEY = "sidebarStateChange";

export const updateLocalStorage = async (key, value) => {
  localStorage.setItem(key, value);
  window.dispatchEvent(
    new CustomEvent(STORAGE_EVENT_KEY, { detail: { key, value } })
  );
};

const AppDrawerNav = () => {
  const [botData, setBotData] = useState({
    ...botDataDefault,
    workflow: {
      retrieval_1: retrievalDefault,
      generation_2: generationDefault,
    },
  });
  const { user, isLoading } = useSelector((state) => state.auth);
  const chatbot = useSelector((state) => state.chatbot);
  const organization = useSelector((state) => state.organization);
  // for app drawer tabs
  const [tabName, setTabName] = useState("createChatbot");
  // show/hide alert for starter with no subscription dialog
  const [openKeys, setOpenKeys] = useState(false);
  // default snackbar for react flow - // remove later
  const [openAlert, setOpenAlert] = useState(false);
  const [message, setMessage] = useState("");

  // react flow snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false); // For snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Message for snackbar
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // Severity of the alert
  const [snackbarDelay, setSnackbarDelay] = useState(6000);

  // store create bot templates
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [nodeCount, setNodeCount] = useState(0);

  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // onboarding
  const [run, setRun] = useState(false); // Controls whether the tour should run
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [canStartTour, setCanStartTour] = useState(false);

  // delete tool states
  const [deleted, setDeleted] = useState([]);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const handleDeleteSelected = (nodeId) => {
    const selected = [{ id: nodeId }];
    if (selected.length === 0) return;

    // turn them into "remove" changes
    const removeChanges = selected.map((el) => ({
      id: el.id.includes("retrieval")
        ? el.id.replace("retrieval", "QueryloopNode")
        : el.id,
      type: "remove",
    }));

    // feed them through your existing logic
    onNodesChange(removeChanges);
  };

  const onNodesChange = (changes) => {
    const deleteChanges = changes.filter((change) => change.type === "remove");
    if (deleteChanges.length > 0) {
      setDeleted(deleteChanges.map((change) => change.id)); // Store IDs of nodes to be deleted
      setOpenDeleteConfirmation(true);
      return; // Prevent ReactFlow from automatically deleting
    }
    setElements((nds) => applyNodeChanges(changes, nds));
  };

  const handleDeleteConfirm = async () => {
    setElements((nds) => nds.filter((node) => !deleted.includes(node.id))); // Manually remove nodes
    setEdges((eds) =>
      eds.filter(
        (edge) =>
          !deleted.includes(edge.source) && !deleted.includes(edge.target)
      )
    );
    await clearDb();
    // Remove from botData.workflow
    const updatedWorkflow = { ...botData.workflow };
    deleted.forEach((id) => {
      const p = id.split("_") || [0, 1];
      let node_to_delete =
        p[0] === "QueryloopNode" ? "retrieval" : "generation";
      delete updatedWorkflow[`${node_to_delete}_${p[1]}`];
    });
    setBotData((prevData) => ({
      ...prevData,
      datasets: deleted[0].includes("QueryloopNode") ? [] : prevData.datasets,
      structured_datasets: deleted[0].includes("QueryloopNode")
        ? []
        : prevData.structured_datasets,
      finetuning_dataset: deleted[0].includes("generation")
        ? {}
        : prevData.finetuning_dataset,
      dynamicShot_dataset: deleted[0].includes("generation")
        ? []
        : prevData.dynamicShot_dataset,
      columnTypes: deleted[0].includes("generation")
        ? {}
        : prevData.columnTypes,
      metadataDescription: deleted[0].includes("QueryloopNode")
        ? {}
        : prevData.metadataDescription,
      headers: deleted[0].includes("QueryloopNode") ? [] : prevData.headers,
      unstructuredColumnTypes: deleted[0].includes("QueryloopNode")
        ? {}
        : prevData.unstructuredColumnTypes,
      structured_file: deleted[0].includes("QueryloopNode")
        ? null
        : prevData.structured_file,
      workflow: updatedWorkflow,
    }));

    setOpenDeleteConfirmation(false); // Close the confirmation dialog
  };
  // Open snackbar with a message and severity
  const showSnackbar = (message, severity = "info", delay = 6000) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
    setSnackbarDelay(delay);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // node states global
  const initialNodes = [
    {
      id: "InputNodes",
      type: "InputNodes",
      data: {
        botData: botData,
        setBotData: setBotData,
        handleInputsTutorial: () =>
          handleInputsTutorial(
            setRun,
            setStepIndex,
            getInitialInputSteps,
            setSteps
          ),
        setMessage: setMessage,
        setOpenAlert: setOpenAlert,
        setOpenKeys: setOpenKeys,
        showSnackbar,
        handleDeleteSelected,
      },
      position: { x: 0, y: 50 },
    },

    {
      id: "QueryloopNode_1",
      type: "QueryloopNode",
      data: {
        botData: {
          ...botData,
          workflow: {
            ...botData.workflow,
            [`retrieval_1`]: retrievalDefault,
          },
          structured_datasets: botDataDefault?.structured_datasets,
          unstructuredColumnTypes: botDataDefault?.unstructuredColumnTypes,
          columnTypes: botDataDefault?.columnTypes || {},
          datasets: botDataDefault?.datasets,
          csvData: botDataDefault?.csvData,
          query_preprocessing_prompts:
            botDataDefault?.query_preprocessing_prompts,
        },
        setBotData: setBotData,
        nodeId: "retrieval_1",
        openAlert: openAlert,
        setOpenAlert: setOpenAlert,
        message: message,
        setMessage: setMessage,
        setOpenKeys,
        openKeys,
        handleRetrievalTutorial,
        showSnackbar,
        handleDeleteSelected,
      },
      position: { x: 450, y: 50 },
    },

    {
      id: "generation_2",
      type: "llm",
      data: {
        botData: {
          ...botData,
          workflow: {
            ...botData.workflow,
            [`generation_2`]: generationDefault,
          },
        },
        setBotData: setBotData,
        nodeId: "generation_2",
        setMessage: setMessage,
        setOpenAlert: setOpenAlert,
        handleGenerationTutorial: handleGenerationTutorial,
        showSnackbar,
        handleDeleteSelected,
      },
      position: { x: 1400, y: 50 },
    },
  ];

  // create app edges/connections for nodes
  const [_EDGES_, _SET_EDGES_] = useState({});
  const [_EDGE_LENGTH_, _SET_EDGE_LENGTH_] = useState(0);
  const [reactFlowInstance, setReactFlowInstance] = useState(null); // test later
  const [elements, setElements] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([
    {
      id: "e-problemstatement-BasePrompt",
      source: "InputNodes",
      animated: true,
      target: "QueryloopNode",
      arrowHeadType: "arrowclosed",
    },
  ]);

  const [nodeTypesList, setNodeTypesList] = useNodesState(initialNodesTypes);
  const [edgeTypesList, setEdgeTypesList] = useEdgesState(initialEdgeTypes);

  // info sidebar
  const [type, set_type] = useState("");
  const [open_sidebar, set_open_sidebar] = useState(false);
  const sidebarRef = useRef(null);

  // logrocket set user
  useEffect(() => {
    const url = window.location.href.split("/");
    const domain = "app.queryloop.ai";
    if (url[2] === domain) {
      const userName = user?.user?.first_name + " " + user?.user?.last_name;
      const userEmail = user?.user?.email;
      if (userName && userEmail) {
        LogRocket.identify(userEmail, {
          userName: userName,
          userEmail: userEmail,
        });
      }
    }
  }, []);

  // start onboarding tour at open app if first_time_user and openKeysPopup is closed
  useEffect(() => {
    const url = window.location.href.split("/");
    const name =
      url[url.length - 1] === "" ? url[url.length - 2] : url[url.length - 1];
    if (
      user?.user?.first_time_user === true &&
      canStartTour &&
      name === "createChatbot"
    ) {
      startTour(setRun, setSteps);
    }
  }, [user?.user?.first_time_user, canStartTour]);

  // set sidebar in local storage
  useEffect(() => {
    const handleStateChange = () => {
      const storedType = localStorage.getItem("type");
      const storedOpenSidebar = localStorage.getItem("open_sidebar") === "true";

      set_type(storedType || "");
      set_open_sidebar(storedOpenSidebar);
    };

    const handleCustomEvent = (event) => {
      if (event.detail.key === "open_sidebar" || event.detail.key === "type") {
        handleStateChange();
      }
    };

    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        set_open_sidebar(false);
        localStorage.setItem("open_sidebar", "false");
        localStorage.setItem("type", "");
        updateLocalStorage("open_sidebar", "false");
        updateLocalStorage("type", "");
      }
    };

    handleStateChange();

    window.addEventListener("storage", handleStateChange);
    window.addEventListener(STORAGE_EVENT_KEY, handleCustomEvent);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("storage", handleStateChange);
      window.removeEventListener(STORAGE_EVENT_KEY, handleCustomEvent);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // clean up storage at every refresh
  useEffect(() => {
    const cleanupLocalStorage = () => {
      const user = localStorage.getItem("user");
      const token = localStorage.getItem("token");

      localStorage.clear();
      if (isDatabaseEmpty()) {
        clearDb();
      }

      if (user) localStorage.setItem("user", user);
      if (token) localStorage.setItem("token", token);
    };

    cleanupLocalStorage();
    window.addEventListener("beforeunload", cleanupLocalStorage);

    return () => {
      window.removeEventListener("beforeunload", cleanupLocalStorage);
    };
  }, []);

  // logout from app
  const handleLogout = useCallback(() => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.location.href = "/";
    dispatch(resetUserComplete());
    dispatch(resetChatbot());
  }, [dispatch]);

  // set user and org ids
  const id = user?.user?._id["$oid"];
  const orgId = organization?.organization?._id?.["$oid"];

  // stripe pages
  useEffect(() => {
    const queryString = window.location.search;
    if (queryString === "?success=true") {
      showSnackbar(
        "You have successfully subscribed to Queryloop Pro",
        "success"
      );
    } else if (queryString === "?success=false") {
      showSnackbar(
        "Your subscription to Queryloop Pro has failed. Please try again later",
        "error"
      );
    } else if (queryString === "?credits=true") {
      showSnackbar("New credits have been added to your account", "success");
    } else if (queryString === "?credits=false") {
      showSnackbar(
        "New credits could not be added to your account. Please try again later",
        "error"
      );
    }
  }, []);

  // handle drawer click
  useEffect(() => {
    const url = window.location.href.split("/");
    const name = url[url.length - 1] || url[url.length - 2];
    const secondLastSegment = url[url.length - 2];

    if (secondLastSegment === "deployedBots" || name === "deployedBots") {
      setTabName("deployedBots");
    } else if (name === "createChatbot") {
      if (
        user?.user?.privilege === "owner" ||
        user?.user?.privilege === "admin"
      ) {
        setTabName("createChatbot");
      } else {
        setTabName("deployedBots");
      }
    } else if (
      name === "myBots" ||
      name === "list" ||
      name === "testanddeploy" ||
      name.search("id=") === 0
    ) {
      setTabName("myBots");
    } else if (
      name === "settingsPage" ||
      name === "myKeys" ||
      name === "usage" ||
      name === "currentPlan" ||
      name === "subscription" ||
      name === "apiKeys" ||
      name === "verificationToken" ||
      name === "select-llm"
    ) {
      if (
        user?.user?.privilege === "owner" ||
        user?.user?.privilege === "admin"
      ) {
        setTabName("settingsPage");
      }
    } else if (name === "Management") {
      if (
        (user?.user?.privilege === "owner" ||
          user?.user?.privilege === "admin") &&
        organization?.organization?.subscription !== "starter"
      ) {
        setTabName("Management");
      } else if (organization?.organization?.subscription === "starter") {
        showSnackbar("You need a pro account to access this page!", "error");
      }
    } else if (name === "profile") {
      setTabName("profile");
    } else if (name === "deployedBots") {
      setTabName("deployedBots");
    } else if (name === "contactSupport") {
      setTabName("contactSupport");
    } else if (name === "main") {
      setTabName("main");
    }
  }, [window.location.href, tabName, setTabName]);

  // get org if user is not null
  useEffect(() => {
    const local_user = JSON.parse(localStorage.getItem("user"));
    if (user === null || local_user === null) {
      alert("Session expired. Please, Login Again!");
      handleLogout();
    }

    if (!organization?.organization) {
      if (id) {
        try {
          dispatch(get_organization_by_user(id));
        } catch (error) {
          sendBugReport(
            organization?.organization,
            user?.user,
            error,
            "Getting organization",
            dispatch
          );
        }
      }
    }

    setBotData({ ...botData, organization: orgId, creator: id });
  }, [user]);

  // set botname
  useEffect(() => {
    if (
      botData.name === "Untitled application" &&
      organization?.organization?.name
    ) {
      const defaultName = `${organization.organization.name} ${
        organization.organization.bot?.length || ""
      }`.trim();
      setBotData((prevData) => ({ ...prevData, name: defaultName }));
    }

    if (
      parseInt(botData.name.split(" ")[1]) <
      organization?.organization?.bot?.length
    ) {
      const defaultName = `${organization.organization.name} ${
        organization.organization.bot?.length || ""
      }`.trim();
      setBotData((prevData) => ({ ...prevData, name: defaultName }));
    }
  }, [organization]);

  // get ret and gen and ft combinations if bot_id exists
  // get chatbots if chatbot.chatbots.length < 1
  const reload = async (tab_id = null) => {
    const bot_id = chatbot?.combinationUsed;
    if (bot_id) {
      const combinationItem = chatbot?.chatbots?.combinations?.find(
        (combination) => combination._id["$oid"] === bot_id
      );

      if (combinationItem?.initialised !== "initialised") {
        //await dispatch(getBotbyOrg(orgId));
        await dispatch(getBotsbyUser(id));
      }

      // Fetch retrieval combinations if they exist
      if (
        organization?.organization?.bot?.length > 0 &&
        combinationItem?.combinations?.length > 0 &&
        (tab_id === "1" || tab_id === "5")
      ) {
        if (bot_id) {
          try {
            await dispatch(getBotbyID({ bot_id }));
            const resp = await dispatch(
              getCombinations({ id: bot_id, items: 10, from: 0 })
            );
          } catch (error) {
            sendBugReport(
              organization?.organization,
              user?.user,
              error,
              "Getting retrieval combinations",
              dispatch
            );
          }
        }
      }

      if (
        organization?.organization?.bot?.length > 0 &&
        combinationItem?.generation_combinations?.length > 0 &&
        (tab_id === "7" || tab_id === "5")
      ) {
        await dispatch(getBotbyID({ bot_id }));
        try {
          const response = await dispatch(getgenerationCombinations(bot_id));
        } catch (error) {
          sendBugReport(
            organization?.organization,
            user?.user,
            error,
            "Getting generation foundation combinations",
            dispatch
          );
        }
        if (combinationItem?.finetune_combinations?.length > 0) {
          try {
            const response = await dispatch(getFinetuningCombinations(bot_id));
          } catch (error) {
            sendBugReport(
              organization?.organization,
              user?.user,
              error,
              "Getting generation finetune combinations",
              dispatch
            );
          }
        }
      }
    }

    // Fetch bots if there are no chatbots loaded but the organization has bots
    if (
      chatbot.chatbots.length < 1 &&
      organization?.organization?.bot?.length > 0
    ) {
      // await dispatch(getBotbyOrg(orgId));
      await dispatch(getBotsbyUser(id));
    }

    // if tab is 0 (bots list)
    if (tab_id === "0") {
      // await dispatch(getBotbyOrg(orgId));
      await dispatch(getBotsbyUser(id));
    }
  };

  // get org by user id
  const reloadOrganization = async () => {
    if (user === null) {
      alert("Session expired. Please, Login Again!");
      handleLogout();
    }
    if (!organization?.organization) {
      if (id) {
        dispatch(get_organization_by_user(id));
      }
    }
  };

  // get org from org id
  const reloadUser = async () => {
    if (organization?.organization?.bot?.length > 0) {
    } else {
      const orgId = organization?.organization?._id["$oid"];
      if (orgId) {
        await dispatch(getOrganization(orgId));
      }
    }
  };

  // if auth loading is true, show loader component
  if (isLoading === true) {
    return <Loader />;
  }

  return (
    <div style={drawerContainer}>
      {/* main app drawer */}

      <AppSidebar
        windowWidth={windowWidth}
        tabName={tabName}
        setTabName={setTabName}
        navigate={navigate}
        user={user}
        handleLogoutApp={handleLogout}
        subscription={organization?.organization?.subscription}
        privilege={user?.user?.privilege}
        experimentsCount={organization?.organization?.bot?.length}
      />
      <Box sx={drawerSubContainer}>
        {/* sidebar component*/}
        {open_sidebar && (
          <SideBar
            type={type}
            open_sidebar={open_sidebar}
            set_open_sidebar={set_open_sidebar}
            sidebarRef={sidebarRef}
          />
        )}

        {/* GraphRAG Snackbar */}
        <GraphRAGSnackBar
          openKeys={openKeys}
          setOpenKeys={setOpenKeys}
          navigate={navigate}
        />

        {/* create chat bot snackbar general*/}
        <CreateChatBotSnackBarGeneral
          openAlert={openAlert}
          setOpenAlert={setOpenAlert}
          setMessage={setMessage}
          message={message}
        />

        {/* payment success dialog */}
        <PaymentSuccessDialog
          setShowPaymentSuccess={setShowPaymentSuccess}
          showPaymentSuccess={showPaymentSuccess}
        />
        <CssBaseline />

        {/* check: appbar not being used now */}
        {/* general all snackbar */}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={snackbarDelay} // Closes after 6 seconds
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }} // Position of the snackbar
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* main component corresponding to drawer click */}
        <Box component="main" sx={drawerMain}>
          {tabName === "createChatbot" ? (
            <ReactFlowProvider>
              <Suspense>
                <BotType
                  setTabName={setTabName}
                  botData={botData}
                  setBotData={setBotData}
                  nodeCount={nodeCount}
                  setNodeCount={setNodeCount}
                  openKeys={openKeys}
                  setOpenKeys={setOpenKeys}
                  openAlert={openAlert} // remove later
                  setOpenAlert={setOpenAlert} // remove later
                  message={message} // remove later
                  setMessage={setMessage} // remove later
                  _EDGES_={_EDGES_}
                  _SET_EDGES_={_SET_EDGES_}
                  _EDGE_LENGTH_={_EDGE_LENGTH_}
                  _SET_EDGE_LENGTH_={_SET_EDGE_LENGTH_}
                  reactFlowInstance={reactFlowInstance}
                  elements={elements}
                  setElements={setElements}
                  onNodesChange={onNodesChange}
                  edges={edges}
                  setEdges={setEdges}
                  onEdgesChange={onEdgesChange}
                  nodeTypesList={nodeTypesList}
                  setNodeTypesList={setNodeTypesList}
                  edgeTypesList={edgeTypesList}
                  setEdgeTypesList={setEdgeTypesList}
                  initialNodesTypes={initialNodesTypes}
                  startTour={startTour}
                  handleDragDropSuccessRet={
                    selectedOption != null
                      ? () =>
                          handleDragDropSuccessRet(
                            setSteps,
                            steps,
                            selectedOption,
                            getInitialRetrievalSteps,
                            setRun,
                            setStepIndex
                          )
                      : null
                  }
                  handleDragDropSuccessGen={
                    selectedOption != null
                      ? () =>
                          handleDragDropSuccessGen(
                            steps,
                            setSteps,
                            setStepIndex,
                            setRun
                          )
                      : null
                  }
                  handleRetrievalTutorial={() =>
                    handleRetrievalTutorial(setRun, setStepIndex, setSteps)
                  }
                  handleGenerationTutorial={() =>
                    handleGenerationTutorial(setRun, setStepIndex, setSteps)
                  }
                  selectedTemplate={selectedTemplate}
                  setSelectedTemplate={setSelectedTemplate}
                  showSnackbar={showSnackbar}
                  handleDeleteSelected={handleDeleteSelected}
                  openDeleteConfirmation={openDeleteConfirmation}
                  setOpenDeleteConfirmation={setOpenDeleteConfirmation}
                  handleDeleteConfirm={handleDeleteConfirm}
                />
              </Suspense>
            </ReactFlowProvider>
          ) : null}

          {tabName === "myBots" ? (
            <Suspense>
              <PostGrid
                setTabName={setTabName}
                reloadUser={reloadUser}
                reload={reload}
                bot_id={chatbot?.combinationUsed || ""}
                user_id={id}
                showSnackbar={showSnackbar}
              />
            </Suspense>
          ) : null}

          {tabName === "settingsPage" ? (
            <SettingsPage
              setTabName={setTabName}
              reloadUser={reloadUser}
              reload={reloadOrganization}
              userId={id}
              showSnackbar={showSnackbar}
            />
          ) : null}

          {tabName === "Management" ? <Management reload={reload} /> : null}

          {tabName === "profile" ? <UserProfile reload={reload} /> : null}

          {tabName === "deployedBots" ? <DeployedBots reload={reload} /> : null}

          {tabName === "contactSupport" ? <ContactUs reload={reload} /> : null}

          {tabName === "main" ? (
            <ReactFlowProvider>
              <Templates
                setTabName={setTabName}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                setNodeCount={setNodeCount}
                initialNodes={initialNodes}
                setElements={setElements}
                setBotData={setBotData}
                setNodeTypesList={setNodeTypesList}
                nodeTypesList={nodeTypesList}
              />
            </ReactFlowProvider>
          ) : null}
        </Box>
        <Onboarding
          run={run}
          steps={steps}
          setSteps={setSteps}
          setRun={setRun}
          stepIndex={stepIndex}
          setStepIndex={setStepIndex}
          setSelectedOption={setSelectedOption}
          selectedOption={selectedOption}
          user_id={id}
        />
      </Box>
    </div>
  );
};

export default AppDrawerNav;
